import type { Stand } from '@vv/common';
import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useApi } from '../../api/useApi';
import { useAuth } from '../../auth/AuthProvider';
import { SignInPanel } from '../../auth/SignInPanel';
import { useChat } from '../../chat/useChat';
import {
  fetchLoginConfig,
  fetchNotificationsEnabled,
  fetchVideoRoom,
  toggleNotifications,
  VIDEO_INVITE,
  type ChatMessage,
} from '../../legacy-api/chat';
import { legacyFairDomain } from '../../legacy-api/client';
import { formatDate, formatTime } from '../../utils/format';
import { SendIcon } from '../icons';

/**
 * Chat with an exhibitor, through the legacy API (see `legacy-api/client.ts`),
 * gated the way the legacy app gated it: chat enabled on the stand, the fair
 * running, and the visitor signed in with Facebook or Google.
 */
export function ChatTab({ stand }: { stand: Stand }): ReactElement {
  const { t } = useTranslation();
  const { token } = useAuth();
  const config = useApi(() => fetchLoginConfig(legacyFairDomain()));

  if (!stand.chatEnabled) {
    return <Notice>{t('chat.unavailable')}</Notice>;
  }

  if (config.loading) {
    return <Notice>{t('common.loading')}</Notice>;
  }

  if (config.data === undefined) {
    return <Notice>{t('common.error')}</Notice>;
  }

  if (!config.data.active) {
    return <Notice>{t('chat.fairClosed')}</Notice>;
  }

  return token === undefined ? (
    <SignInPanel config={config.data} title={t('chat.signInTitle')} body={t('chat.signInBody')} />
  ) : (
    // Keyed so another stand's chat starts from an empty thread.
    <ChatRoom key={stand.id} stand={stand} token={token} />
  );
}

function Notice({ children }: { children: ReactNode }): ReactElement {
  return <p className="py-8 text-center text-fog-500">{children}</p>;
}

function ChatRoom({ stand, token }: { stand: Stand; token: string }): ReactElement {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const { messages, loading, failed, send } = useChat(stand.id);
  const videoRoom = useApi(() => fetchVideoRoom(token, stand.id), [token, stand.id]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);

  // Keep the newest message in view as replies arrive.
  useEffect(() => {
    const thread = threadRef.current;

    if (thread !== null) {
      thread.scrollTop = thread.scrollHeight;
    }
  }, [messages.length]);

  const videoUrl =
    videoRoom.data === undefined ? undefined : `https://meet.jit.si/${videoRoom.data}`;

  const submit = (event: FormEvent): void => {
    event.preventDefault();

    const content = draft.trim();

    if (content === '' || sending) {
      return;
    }

    setSending(true);
    setSendFailed(false);
    send(content)
      .then(() => {
        setDraft('');
      })
      .catch(() => {
        setSendFailed(true);
      })
      .finally(() => {
        setSending(false);
      });
  };

  return (
    <div className="space-y-3 py-4">
      <div className="flex items-center justify-between gap-3 text-sm">
        {videoUrl === undefined ? (
          <span />
        ) : (
          <a href={videoUrl} target="_blank" rel="noreferrer noopener" className="text-primary">
            {t('chat.videoChat')}
          </a>
        )}
        <button type="button" onClick={signOut} className="text-fog-500 hover:text-fog-300">
          {t('chat.signOut')}
        </button>
      </div>

      {!stand.chatOnline && (
        <p className="rounded-lg bg-ink-850 px-3 py-2 text-sm text-fog-500">
          {t('chat.offline', { title: stand.title })}
        </p>
      )}

      <div
        ref={threadRef}
        aria-live="polite"
        className="max-h-[55vh] min-h-40 space-y-2 overflow-y-auto rounded-xl bg-ink-900 p-3"
      >
        {stand.helloMessage !== null && stand.helloMessage !== '' && (
          <Bubble fromExhibitor>{stand.helloMessage}</Bubble>
        )}
        {loading && <p className="text-center text-sm text-fog-600">{t('common.loading')}</p>}
        {failed && <p className="text-center text-sm text-red-400">{t('chat.loadFailed')}</p>}
        <Thread messages={messages} videoUrl={videoUrl} />
      </div>

      <form onSubmit={submit} className="flex items-center gap-2">
        <input
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          placeholder={t('chat.placeholder')}
          aria-label={t('chat.placeholder')}
          maxLength={2000}
          className="min-w-0 flex-1 rounded-xl bg-ink-850 px-4 py-3 text-fog-100 placeholder:text-fog-600"
        />
        <button
          type="submit"
          disabled={sending || draft.trim() === ''}
          aria-label={t('chat.send')}
          className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary text-white disabled:opacity-50"
        >
          <SendIcon className="size-5" />
        </button>
      </form>
      {sendFailed && (
        <p role="alert" className="text-sm text-red-400">
          {t('chat.sendFailed')}
        </p>
      )}

      <NotificationToggle token={token} />
    </div>
  );
}

/** Messages grouped under a date heading per day, as the legacy chat showed them. */
function Thread({
  messages,
  videoUrl,
}: {
  messages: ChatMessage[];
  videoUrl: string | undefined;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <>
      {messages.map((message, index) => {
        const day = formatDate(message.createdAt);
        const previous = messages[index - 1];
        const heading =
          previous !== undefined && formatDate(previous.createdAt) === day ? null : day;
        const fromExhibitor = message.stand !== null && message.stand !== undefined;

        return (
          <div key={message.id}>
            {heading !== null && <p className="py-2 text-center text-xs text-fog-600">{heading}</p>}
            <Bubble fromExhibitor={fromExhibitor} time={formatTime(message.createdAt)}>
              {message.content === VIDEO_INVITE ? (
                <>
                  {t('chat.videoInvite')}{' '}
                  {videoUrl !== undefined && (
                    <a
                      href={videoUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="underline"
                    >
                      {t('chat.joinVideo')}
                    </a>
                  )}
                </>
              ) : (
                message.content
              )}
            </Bubble>
          </div>
        );
      })}
    </>
  );
}

function Bubble({
  fromExhibitor,
  time,
  children,
}: {
  fromExhibitor: boolean;
  time?: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className={`flex ${fromExhibitor ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm break-words whitespace-pre-wrap ${
          fromExhibitor ? 'bg-ink-800 text-fog-100' : 'bg-primary text-white'
        }`}
      >
        {children}
        {time !== undefined && time !== '' && (
          <span className="mt-1 block text-right text-[11px] opacity-60">{time}</span>
        )}
      </div>
    </div>
  );
}

function NotificationToggle({ token }: { token: string }): ReactElement | null {
  const { t } = useTranslation();
  const setting = useApi(() => fetchNotificationsEnabled(token), [token]);
  const [enabled, setEnabled] = useState<boolean>();
  const [saving, setSaving] = useState(false);
  const checked = enabled ?? setting.data;

  if (checked === undefined) {
    return null;
  }

  return (
    <label className="flex items-center gap-2 text-sm text-fog-500">
      <input
        type="checkbox"
        checked={checked}
        disabled={saving}
        onChange={() => {
          setSaving(true);
          toggleNotifications(token)
            .then(setEnabled)
            .catch(() => undefined)
            .finally(() => {
              setSaving(false);
            });
        }}
      />
      {t('chat.notifications')}
    </label>
  );
}
